import type { CSSProperties } from 'react';

import { Button, Icon } from '@/components/ui';
import { useModal } from '@/components/modal/useModal';
import { isBulkJobActive, jobLabel, useBulkJobsStore } from '@/realtime/bulkJobsStore';

const STAT_BASE: CSSProperties = {
  padding: 16,
  borderRadius: 'var(--radius-md)',
  textAlign: 'center',
  border: '1px solid var(--border)',
};

interface BulkJobProgressDialogProps {
  jobId: string;
  /** Snapshot from the 202 response, shown until the first live push lands. */
  initialTotal: number;
}

/**
 * Blocking progress dialog for a "select all in set" bulk operation. Driven by
 * bulkJobsStore (SignalR pushes / polling fallback — see BulkJobsListener).
 * "Run in background" just closes the modal; the job keeps going and the
 * top-bar JobsIndicator + completion toast take over.
 */
export function BulkJobProgressDialog({ jobId, initialTotal }: BulkJobProgressDialogProps) {
  const { closeModal } = useModal();
  const job = useBulkJobsStore((s) => s.jobs[jobId]);

  const status = job?.status ?? 'queued';
  const total = job?.total ?? initialTotal;
  const processed = job?.processed ?? 0;
  const affected = job?.affected ?? 0;
  const skipped = job?.skipped ?? 0;
  const isImport = job?.jobType === 'import';
  const created = job?.created ?? 0;
  const updated = job?.updated ?? 0;
  const failed = job?.failed ?? 0;
  const active = isBulkJobActive(status);
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const label = job ? jobLabel(job) : 'Operation';

  return (
    <>
      <div className="ds-modal-head">
        <h2>{active ? `${label} in progress` : `${label} ${status}`}</h2>
      </div>
      <div className="ds-modal-body">
        {active ? (
          <>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-2)' }}>
              {status === 'queued'
                ? isImport
                  ? 'Waiting for a worker — your file is queued…'
                  : `Waiting for a worker — ${total.toLocaleString()} contacts queued…`
                : `Processing ${total.toLocaleString()} ${isImport ? 'rows' : 'contacts'}…`}
            </p>
            <div
              style={{
                height: 8,
                borderRadius: 4,
                background: 'var(--border)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 4,
                  background: 'var(--brand)',
                  transition: 'width 300ms ease',
                }}
              />
            </div>
            <div
              style={{
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--text-3)',
              }}
            >
              <span>
                {processed.toLocaleString()} / {total.toLocaleString()} processed
              </span>
              <span>{pct}%</span>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: status === 'failed' ? 'var(--red-soft)' : 'var(--green-soft)',
                color: status === 'failed' ? 'var(--red)' : '#086940',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                marginBottom: 16,
              }}
            >
              <Icon name={status === 'failed' ? 'alert' : 'check'} size={14} />
              {status === 'failed'
                ? job?.error ?? `${label} failed.`
                : `${label} completed.`}
            </div>
            {isImport ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <Stat label="Total" value={total} tone="brand" />
                <Stat label="Created" value={created} tone="green" />
                <Stat label="Updated" value={updated} tone="green" />
                <Stat
                  label={failed > 0 ? 'Errors' : 'Skipped'}
                  value={failed > 0 ? failed : skipped}
                  tone={failed > 0 ? 'red' : skipped > 0 ? 'red' : 'slate'}
                />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <Stat label="Total" value={total} tone="brand" />
                <Stat label="Affected" value={affected} tone="green" />
                <Stat label="Skipped" value={skipped} tone={skipped > 0 ? 'red' : 'slate'} />
              </div>
            )}
          </>
        )}
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        {active ? (
          <Button variant="secondary" onClick={closeModal}>
            Run in background
          </Button>
        ) : (
          <Button variant="primary" onClick={closeModal}>
            Close
          </Button>
        )}
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'brand' | 'green' | 'red' | 'slate';
}) {
  const TONE: Record<typeof tone, { bg: string; fg: string }> = {
    brand: { bg: 'var(--brand-soft)', fg: 'var(--brand)' },
    green: { bg: 'var(--green-soft)', fg: 'var(--green)' },
    red: { bg: 'var(--red-soft)', fg: 'var(--red)' },
    slate: { bg: 'var(--slate-soft)', fg: 'var(--slate)' },
  };
  const c = TONE[tone];
  return (
    <div style={{ ...STAT_BASE, background: c.bg, borderColor: 'transparent' }}>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 22,
          fontWeight: 600,
          color: c.fg,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
        }}
      >
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: 11.5, color: c.fg, marginTop: 4 }}>{label}</div>
    </div>
  );
}
